import type { ReactNode } from 'react'
import { formatPercent } from '../projectionsFormat'
import type { CashPolicy } from '../engine/types'

type YieldLineProps = {
  snapshot: CashPolicy['sweep']['yield_snapshot']
  stale?: boolean
  unavailable?: boolean
}

function aprAt(snapshot: NonNullable<CashPolicy['sweep']['yield_snapshot']>, index: number): number {
  return snapshot.apr_by_month[index] ?? 0
}

export function moneyMarketYieldTooltipContent(
  snapshot: CashPolicy['sweep']['yield_snapshot'],
): ReactNode {
  if (!snapshot) {
    return <p className="text-sm text-gray-600">Interest modelled at 0%.</p>
  }
  const spreadPct = formatPercent(snapshot.spread)
  const nowPct = formatPercent(aprAt(snapshot, 0))
  const t12Pct = formatPercent(aprAt(snapshot, 11))
  const t24Pct = formatPercent(aprAt(snapshot, 23))
  const line = `Yield: Treasury curve (FRED, as of ${snapshot.as_of}) + ${spreadPct} → ${nowPct} now · ${t12Pct} at +12 · ${t24Pct} at +24`
  return (
    <>
      <p className="text-sm text-gray-900">{line}</p>
      <p className="mt-2 text-sm text-gray-600">
        Rates are bootstrapped from FRED Treasury constant-maturity series plus the money-market
        spread configured in projections settings ({spreadPct}).
      </p>
    </>
  )
}

export default function YieldLine({ snapshot, stale = false, unavailable = false }: YieldLineProps) {
  if (unavailable || !snapshot) {
    return <p className="text-xs text-amber-800">Interest modelled at 0%</p>
  }
  if (stale) {
    return (
      <p className="text-xs text-amber-800">
        FRED unavailable — using cached curve from {snapshot.as_of}
      </p>
    )
  }
  return null
}
